import React, { useState } from "react";
import _ from "lodash";
import { Action } from "typescript-fsa";
import { Dispatch } from "redux";
import {
    Form,
    Input,
    InputGroup,
    InputGroupText,
    Button,
} from "reactstrap";
import { connect } from "react-redux";
import { History } from "history";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { useIntl } from "react-intl";

import { WaIcon } from "@ui/WaIcon";
import { Spinner } from "@ui/Spinner";
import { compose } from "@util/compose";
import { search } from "../../actions";
import { SearchState as State } from "../../models";
import {
    getSearchIsLoading,
    makeOnSearchPage,
    makeGetSearchQueryFromProps,
} from "../../selectors";
import { messages } from "../../messages";

interface SearchStateProps {
    loading: boolean;
    query: string;
    onSearchPage: boolean;
}

interface SearchDispatchProps {
    searchHandler: (payload: string, history: History) => Action<string>;
}

type Props = SearchStateProps & SearchDispatchProps & RouteComponentProps<any>;

const Search = ({ searchHandler, loading, query: propsQuery, onSearchPage, history }: Props) => {
    const intl = useIntl();
    const [query, setQuery] = useState<string>(propsQuery);
    const [focused, setFocused] = useState<boolean>(false);

    let className = "search-form";
    let placeholder = "";
    if ((onSearchPage && query !== "") || focused) {
        className += " search-form--focused";
        placeholder = intl.formatMessage(messages.searchFormPlaceholderFocused);
    }
    else {
        placeholder = intl.formatMessage(messages.searchFormPlaceholder);
    }
    let addon = (
        <Button type="submit" color="primary">
            <WaIcon name="search" />
        </Button>
    );
    if (loading) {
        addon = <InputGroupText><Spinner /></InputGroupText>;
    }

    // The handlers
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        searchHandler(query, history);
    };
    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const q = event.currentTarget.value;
        setQuery(q);
        searchHandler(q, history);
    };

    return (
        <Form className={className} onSubmit={(e) => handleSubmit(e)}>
            <InputGroup>
                <Input type="text" name="q" role="search"
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => handleOnChange(e)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)} />
                {addon}
            </InputGroup>
        </Form>
    );
};

const debouceSearchHandler = (dispatch: Dispatch<State>) => _.debounce(
    (query: string, history: History) => {
        // Dispatch the search event
        const action = dispatch(search(query));
        if (query.length > 2) {
            // Go to the search results page.
            history.push("/search?q=" + encodeURIComponent(query));
        }
        return action;
    }, 200,
);

const makeMapStateToProps = () => {
    const onSearchPage = makeOnSearchPage();
    const getSearchQueryFromProps = makeGetSearchQueryFromProps();
    return (state: State, props: RouteComponentProps<any>) => {
        return {
            loading: getSearchIsLoading(state),
            query: getSearchQueryFromProps(state, props),
            onSearchPage: onSearchPage(state, props),
        };
    };
};

const mapDispatchToProps = (dispatch: Dispatch<State>) => {
    return {
        searchHandler: debouceSearchHandler(dispatch),
    };
};

const ConnectedSearch = compose<Props, {}>(
    withRouter,
    connect(makeMapStateToProps, mapDispatchToProps),
)(Search);

export {
    Search as SearchPresenter,
    ConnectedSearch as Search,
};
